import { t } from 'i18next'
import IBAN from 'iban'
import { List } from 'immutable'
import { AsyncState, loadingAsyncState, unloadedAsyncState, Updater } from 'widgets-for-react'
import { buildValidator, FieldValidatorsBuilder, validatorBuilders } from '../../../../../../Shared/Frontend/validators/validator'
import { SingleValidatorResult, singleValidatorResultBuilders, Validator, ValidatorResult, validatorResultBuilders } from '../../../../../../Shared/Frontend/validators/validatorExpr'
import { dataLayerServices } from '../../../../seo/datalayer'
import { ZipCode } from '../../../utils/ZipcodeChecker'
import type { AnchorProps, ModernBlocksBaseClassNames } from '../../modernBlocks.state'
import { ExpandableItem } from '../expandableBlock/expandableBlock.state'
import { DonationApiResponseParsed, submitForm } from './donationFormBlock.api'
import { isNonBlacklistedPhone, valutaDisplay } from './utils'

export interface PredefinedAmount {
  amount: number
  button: string
  explainerText?: string
}
export interface PredefinedAmounts {
  minimumAmount: number
  maximumAmount: number
  selectedIndex?: number
  manualAmount?: number
  amounts: PredefinedAmount[]
}
export interface PredefinedValues{
  drupalformId: number
}

export const possibleSalutations = ['Man', 'Vrouw', 'Anders', "Zeg_ik_liever_niet"] as const
export type possibleSalutationsType = typeof possibleSalutations[number]
export interface Step3PersonalData {
  email: string
  machtigingSelected: boolean
  iban: string | undefined
  salutation: possibleSalutationsType
  firstName: string
  insertion: string
  lastName: string
  postalCode: string
  houseNumber: string
  houseNumberAddition: string
  invalidHousenumberPostcodeCombination?: boolean
  street: string
  city: string
  phone: string
  acceptNewsLetter: boolean
}

export interface Step2PersonalData {
  email: string
}

export const choiceFormTypes = [
  'single-sign-on',
  'monthly-sign-on',
  'yearly-sign-on',
] as [
  'single-sign-on',
  'monthly-sign-on',
  'yearly-sign-on',
]
export type ChoiceFormType = typeof choiceFormTypes[number]
export const donationFormTypes = ['express', ...choiceFormTypes] as ['express', ...typeof choiceFormTypes]
export type DonationFormType = typeof donationFormTypes[number]
export type PredefinedAmountsType = {
  [K in ChoiceFormType]: PredefinedAmounts
}
export type DonationFormState = ({
  step: '1-Unloaded',
  loader: AsyncState<DonationApiResponseParsed>
} | (
  {
    successUrl: string
    failedUrl: string
    cancelUrl: string
    predefinedAmounts: PredefinedAmountsType
    validator: Validator<Partial<Step3PersonalData>, Partial<Step3PersonalData>>
    validatorResult: ValidatorResult
    formTypeOptions: DonationFormType[]
    selectedFormType: DonationFormType
    submitButton: string
    formValues: PredefinedValues
    monthlyText?: string
    customTitle?: string
    yearlyText?: string
    optionForMachtiging: boolean
    isPropositionDonation?: boolean
    productId: string
    edited?: boolean
  } & ({
      step: '2-AmountForm'
      manualAmountError?: string
      input: Step2PersonalData
      validatorInput: Partial<Step2PersonalData>
    } | {
      step: '3-PersonalForm'
      zipCodeLoader: AsyncState<ZipCode.CheckResponse>
      input: Step3PersonalData
      validatorInput: Partial<Step3PersonalData>
      recaptcha:string
    })
  )
) & DonationFormBlockProps

export type DonationFormStepState<s extends DonationFormState['step']> = Extract<DonationFormState, {step: s}>

type DonationFormBlockProps = {
  blockIndex: number
  formPreset: string
  text?: string
  submitState: AsyncState<{}>
  title: string
  expandableItems: ExpandableItem[]
} & AnchorProps

export type SubmissionData = {
  name: string
}
export type DonationFormBLockClassNames = ModernBlocksBaseClassNames &
Partial<{
  // INSERT BLOCK SPECIFIC CLASSNAMES HERE!
}>

const zipCodeUpdaters:List<Partial<keyof Step3PersonalData>> = List([
  'houseNumber',
  'postalCode'
])

const numericValues:List<Partial<keyof Step3PersonalData>> = List([
  'houseNumber'
])

export const initStep2PersonalData= (): Step2PersonalData => ({
  email: ''
})

export const initStep3PersonalData= (recurring: boolean): Step3PersonalData => ({
  email: '',
  iban: recurring ? '' : undefined,
  machtigingSelected: recurring,
  salutation: 'Zeg_ik_liever_niet',
  firstName: '',
  insertion: '',
  lastName: '',
  postalCode: '',
  houseNumber: '',
  houseNumberAddition: '',
  street: '',
  city: '',
  phone: '',
  acceptNewsLetter: true,
})

export const initStep2 = (apiResponse:DonationApiResponseParsed): Updater<DonationFormState> => s => ({
  ...s,
  step: '2-AmountForm',
  successUrl: apiResponse.successUrl,
  failedUrl: apiResponse.failedUrl,
  cancelUrl: apiResponse.cancelUrl,
  predefinedAmounts: apiResponse.predefinedAmounts,
  selectedFormType: apiResponse.SelectedFormType,
  formTypeOptions: apiResponse.formTypeOptions,
  formValues: apiResponse.formFields,
  submitButton: apiResponse.submitButton,
  customTitle: apiResponse.customTitle,
  validator: apiResponse.SelectedFormType == 'express' ? validators.expressEmailValidator : validators.emptyValidator.setValueToValidate({}, {}),
  validatorResult: validatorResultBuilders.validating(""),
  input: initStep2PersonalData(),
  monthlyText: apiResponse.monthlyText,
  yearlyText: apiResponse.yearlyText,
  validatorInput: {},
  productId: apiResponse.productId,
  optionForMachtiging: apiResponse.optionForMachtiging,
  machtigingSelected: false,
})

const ibanValidatorBuilder:Partial<FieldValidatorsBuilder<Partial<Step3PersonalData>,Partial<Step3PersonalData>>> = {
  iban: [b => b.fromPromise(async (i, _) => i.iban == undefined || IBAN.isValid(i.iban)), validatorBuilders.string('iban', () => t("DonationBlock:DonationForm.Error_iban"))]
}

const singleDonationValidations:Partial<FieldValidatorsBuilder<Partial<Step3PersonalData>,Partial<Step3PersonalData>>> = {
  email: [b => b.isEmail(), validatorBuilders.string("email", () => t("DonationBlock:DonationForm.Error_email_incorrect"))],
  firstName: [b => b.notEmpty(), validatorBuilders.string("firstName", () => t("DonationBlock:DonationForm.Error_firstName"))],
  lastName: [b => b.notEmpty(), validatorBuilders.string("lastName", () => t("DonationBlock:DonationForm.Error_lastName"))],
  postalCode: [b => b.isPostalCode(), validatorBuilders.string("postalCode", () => t("DonationBlock:DonationForm.Error_postalCode"))],
  houseNumber: [b => b.isNumeric(), validatorBuilders.string("houseNumber", () => t("DonationBlock:DonationForm.Error_houseNumber"))],
  phone:[b => (b
    .fromPromise(async(i, _) => i.phone == undefined || i.phone == "")
    .or(b
      .fromPromise(async (_, phone) => isNonBlacklistedPhone(phone))
      .properAnd(b.isPhone())
    )
  ), validatorBuilders.string('phone', () => t("DonationBlock:DonationForm.Error_phone"))],
  ...ibanValidatorBuilder
}

const singleDonationValidator = buildValidator<Partial<Step3PersonalData>>(
  singleDonationValidations
)

const singleDonationWithIbanValidator = buildValidator<Partial<Step3PersonalData>>({
  ...singleDonationValidations,
  ...ibanValidatorBuilder,
})

const expressEmailValidator = buildValidator<Partial<Step2PersonalData>> ({
  email: [b => (b.fromPromise(async(i, _) => i.email == null || i.email == undefined || i.email == "").or(b.isEmail())), validatorBuilders.string("email", () => t("DonationBlock:DonationForm.Error_email_incorrect"))]
})

export const validators = {
  emptyValidator: buildValidator<Partial<Step3PersonalData>> ({}),
  singleDonationValidator,
  singleDonationWithIbanValidator,
  expressEmailValidator,
  recurringDonationValidator: buildValidator<Partial<Step3PersonalData>>({
    email: [b => b.isEmail(), validatorBuilders.string("email", () => t("DonationBlock:DonationForm.Error_email_incorrect"))],
    firstName: [b => b.notEmpty(), validatorBuilders.string("firstName", () => t("DonationBlock:DonationForm.Error_firstName"))],
    lastName: [b => b.notEmpty(), validatorBuilders.string("lastName", () => t("DonationBlock:DonationForm.Error_lastName"))],
    postalCode: [b => b.isPostalCode(), validatorBuilders.string("postalCode", () => t("DonationBlock:DonationForm.Error_postalCode"))],
    houseNumber: [b => b.isNumeric(), validatorBuilders.string("houseNumber", () => t("DonationBlock:DonationForm.Error_houseNumber"))],
    phone:[b => (b
      .fromPromise(async(i, _) => i.phone == undefined || i.phone == "")
      .or(b
        .fromPromise(async (_, phone) => isNonBlacklistedPhone(phone))
        .properAnd(b.isPhone())
      )
    ), validatorBuilders.string('phone', () => t("DonationBlock:DonationForm.Error_phone"))],
    ...ibanValidatorBuilder,
  }),
}

export const donationFormUpdaters = {
  togglePredefinedAmount: (index: number) : Updater<DonationFormState> => s => {
    if(s.step != '2-AmountForm') return s
    let selectedAmount = getAmountsForForm(s)
    const amount = getSelectedAmount(selectedAmount, index)
    selectedAmount.manualAmount = parseInt(amount + "")
    selectedAmount.selectedIndex = selectedAmount.selectedIndex == index ? undefined : index

    let predefinedAmounts = s.predefinedAmounts
    if (s.selectedFormType == 'express') {
      predefinedAmounts['single-sign-on'] = selectedAmount
    } else {
      predefinedAmounts[s.selectedFormType] = selectedAmount
    }
    return {
      ...s,
      predefinedAmounts: predefinedAmounts,
      manualAmountError: undefined,
    }
  },
  updateManualAmount: (newVal: string) : Updater<DonationFormState> => s => {
    if(s.step != '2-AmountForm') return s
    let updatedVal:number | undefined = undefined
    const manualAmount = getManualAmount(s)
    if(newVal != '') {
      // const parsedVal = parseFloat(newVal)
      const parsedVal = parseInt(newVal)
      updatedVal = isNaN(parsedVal) ? manualAmount : parsedVal
    }

    const selectedFormType = getSelectedFormType(s)
    let predefined = s.predefinedAmounts
    predefined[selectedFormType].manualAmount = updatedVal

    let s1 = {
      ...s,
      predefinedAmounts: predefined,
    }

    return {
      ...s1,
      manualAmountError: getErrorForManualAmount(s1) 
    }
  },
  toggleRecurring: (type:string) : Updater<DonationFormState> => s => ({
    ...s,
    selectedFormType: type as DonationFormType
  }),
  transitionFromStep2: (recaptcha:string) : Updater<DonationFormState> => s => {
    if(s.step !== '2-AmountForm') throw new Error("Wrong step for this updater")

    const predefinedAmount = getAmountsForForm(s)
    const manualAmount = getManualAmount(s)

    if(predefinedAmount.selectedIndex == null && (manualAmount == null || manualAmount && manualAmount < (predefinedAmount.minimumAmount))) {
      return {
        ...s,
        manualAmountError: `Het minimale bedrag is ${valutaDisplay(predefinedAmount.minimumAmount)}`
      }
    }

    if(predefinedAmount.selectedIndex == null && (manualAmount == null || manualAmount && manualAmount > (predefinedAmount.maximumAmount))) {
      return {
        ...s,
        manualAmountError: `Het maximale bedrag is ${valutaDisplay(predefinedAmount.maximumAmount)}`
      }
    }

    if(s.selectedFormType == 'express') {
      if(s.validatorResult.values.filter(v => v.kind != 'passed').size > 0)
      {
        return s
      }
      return {...s, submitState: loadingAsyncState(() => submitForm(s,recaptcha))}
    }

    const recurring = isRecurringFormType(s)
    const validator = recurring ? validators.recurringDonationValidator : validators.singleDonationValidator
    const input = initStep3PersonalData(recurring)

    return {
      ...s,
      step:'3-PersonalForm',
      input,
      validatorInput: {},
      validator: validator.setValueToValidate(input, input),
      zipCodeLoader: unloadedAsyncState(),
      recaptcha:""
    }
  },
  inputStep2Updater: (field: keyof Step2PersonalData, newVal: Step2PersonalData[typeof field]) : Updater<DonationFormState> => s => {
    if (s.step === '2-AmountForm') {

      let newState: DonationFormState = {
        ...s, input: {
          ...s.input,
          [field]: newVal
        }
      }

      if (newState.isPropositionDonation && !newState.edited) {
        dataLayerServices.propositionDonationFormStartPushDatalayer()
        newState.edited = true
      }

      return newState
    }
    else return s
  },
  inputStep3Updater: (field: keyof Step3PersonalData, newVal: Step3PersonalData[typeof field]) : Updater<DonationFormState> => s => {
    if (s.step === '3-PersonalForm') {
      if (numericValues.contains(field)) {
        if (newVal != '') {
          const parsedVal = parseInt(newVal !== true ? newVal || '' : '')
          newVal = isNaN(parsedVal) ? s.input[field] : parsedVal.toString()
        }
      }

      let input = { ...s.input, [field]: newVal }

      if (field == "machtigingSelected")
      {
        if (newVal) input.iban = "" 
        else input.iban = undefined
      }

      let newState = { ...s, input: input }

      if (newState.isPropositionDonation && !newState.edited) {
        dataLayerServices.propositionDonationFormStartPushDatalayer()
        newState.edited = true
      }

      return newState
    }
    else return s
  },
  recaptchaUpdater: (r:string): Updater<DonationFormState> => s => {
    if(s.step != "3-PersonalForm") return s
    return {
      ...s,
      recaptcha: r
    }
  },
  step3ValidateFields: (field?: keyof Step3PersonalData): Updater<DonationFormState> => s => {
    if (s.step !== '3-PersonalForm') return s

    if(field && zipCodeUpdaters.contains(field)) s.zipCodeLoader = getPostcodeLoader(s)

    const input:Partial<Step3PersonalData> = field ? {
      ...s.validatorInput,
      [field]: s.input[field]
    } : s.input

    return {...s, validatorInput: input, validator:s.validator.setValueToValidate(s.input, s.input)}
  },
  step3validationUpdater:(u:Updater<DonationFormStepState<'3-PersonalForm'>["validator"]>) : Updater<DonationFormState> => s => {
    if (s.step != '3-PersonalForm') return s
    const newValidator = u(s.validator)
    return getStep3ValidatorResult({...s, validator:newValidator})
  },
  step2ValidateFields: (field?: keyof Step2PersonalData): Updater<DonationFormState> => s => {
    if (s.step !== '2-AmountForm') throw new Error("Wrong place to call step2 validator")

    const input:Partial<Step3PersonalData> = field ? {
      ...s.validatorInput,
      [field]: s.input[field]
    } : s.input

    return {...s, validatorInput: input, validator:s.validator.setValueToValidate(s.input, s.input)}
  },
  step2validationUpdater:(u:Updater<DonationFormStepState<'2-AmountForm'>["validator"]>) : Updater<DonationFormState> => s => {
    if (s.step != '2-AmountForm') throw new Error("Wrong place to call step2 validator updater")

    const newValidator = u(s.validator)
    return getStep2ValidatorResult({...s, validator:newValidator})
  },
  step3ZipCodeLoaderUpdater:(u: Updater<AsyncState<ZipCode.CheckResponse>>): Updater<DonationFormState> => s => {
    if(s.step !== '3-PersonalForm') return s

    const updatedLoader = u(s.zipCodeLoader)

    if(updatedLoader.kind == 'loaded') {
      return {
        ...s,
        zipCodeLoader:updatedLoader,
        input: {
          ...s.input,
          street:updatedLoader.value.Street,
          city:updatedLoader.value.City,
          postalCode: updatedLoader.value.postcode,
          houseNumber: updatedLoader.value.houseNumber.toString(),
          invalidHousenumberPostcodeCombination: (updatedLoader.value.City == null || updatedLoader.value.Street == null)
        }
      }
    }
    return {...s, zipCodeLoader:updatedLoader}
  },
  transitionFromStep3ToStep2: () : Updater<DonationFormState> => s => s.step == '3-PersonalForm' ? {...s, step:'2-AmountForm', manualAmountError: undefined} : s,
  transitionFromStep3: (recaptcha:string) : Updater<DonationFormState> => s => {
    if(s.step !== '3-PersonalForm') return s
    const s1 = donationFormUpdaters.step3ValidateFields()(s) as DonationFormStepState<'3-PersonalForm'>
    if(s.validatorResult.values.every(v => v.kind == 'passed')) {
      dataLayerServices.propositionDonationFormDonatePushDatalayer(s.input.acceptNewsLetter)

      s1.submitState = loadingAsyncState(() => submitForm(s,recaptcha))

      return s1
    }
    return s1
  }
}

export const getErrorForManualAmount = (s:DonationFormStepState<'2-AmountForm' | '3-PersonalForm'>): string | undefined => {
    const predefinedAmount = getAmountsForForm(s)
    const manualAmount = getManualAmount(s)

    if(predefinedAmount.selectedIndex == null && (manualAmount != null && (manualAmount < predefinedAmount.minimumAmount))) {
      return `Het minimale bedrag is ${valutaDisplay(predefinedAmount.minimumAmount)}`
    }

    if(predefinedAmount.selectedIndex == null && (manualAmount != null && (manualAmount > predefinedAmount.maximumAmount))) {
        return `Het maximale bedrag is ${valutaDisplay(predefinedAmount.maximumAmount)}`
    }

    return undefined
} 


export const getAmountsForForm = (s:DonationFormStepState<'2-AmountForm' | '3-PersonalForm'>) : PredefinedAmounts =>
  s.selectedFormType == 'express' ? s.predefinedAmounts['single-sign-on'] : s.predefinedAmounts[s.selectedFormType]

export const getSelectedFormType = (s:DonationFormStepState<'2-AmountForm' | '3-PersonalForm'>) : ChoiceFormType =>
  s.selectedFormType == 'express' ? "single-sign-on" : s.selectedFormType

export const getSelectedAmount = (p : PredefinedAmounts, selectedIndex: number | undefined = p.selectedIndex) => {
  if (selectedIndex == undefined) return undefined
  const amount = p.amounts[selectedIndex].amount
  return amount
}

export const getManualAmount = (s:DonationFormStepState<'2-AmountForm' | '3-PersonalForm'>): number | undefined => {
  const formType: DonationFormType = s.selectedFormType === "express" ? "single-sign-on": s.selectedFormType
  return s.predefinedAmounts[formType].manualAmount
}

const getPostcodeLoader= (s:DonationFormStepState<"3-PersonalForm">): AsyncState<ZipCode.CheckResponse> => {
  const zipCode = s.input.postalCode.replace(" ", "");
  const houseNumber = Number.parseInt(s.input.houseNumber)
  if(!isNaN(houseNumber) && zipCode.length == 6) return loadingAsyncState(() => ZipCode.CheckRequest({postcode: zipCode, houseNumber:houseNumber}))
  return unloadedAsyncState()
}

const getStep3ValidatorResult = (s:DonationFormStepState<'3-PersonalForm'>): DonationFormStepState<'3-PersonalForm'> => {
  let validatorResult = s.validator.evaluateResult()
  return ({...s, validatorResult:validatorResult })
}

const getStep2ValidatorResult = (s:DonationFormStepState<'2-AmountForm'>): DonationFormStepState<'2-AmountForm'> => {
  let validatorResult = s.validator.evaluateResult()
  return ({...s, validatorResult:validatorResult })
}

export const isRecurringFormType = (s: DonationFormStepState<'2-AmountForm' | '3-PersonalForm'>): boolean =>
  ['monthly-sign-on','yearly-sign-on'].indexOf(s.selectedFormType) != -1 || (s.step == '3-PersonalForm' && s.input.machtigingSelected)

export const isFieldRequired = (state: DonationFormStepState<'3-PersonalForm'|'2-AmountForm'>, field:keyof Step3PersonalData): boolean => { 
  if (state.step == '3-PersonalForm') {
    return (isRecurringFormType(state) ? [
      'email',
      'firstName',
      'lastName',
      'postalCode',
      'houseNumber',
      'iban',
    ] : [
      'email',
      'firstName',
      'lastName',
      'postalCode',
      'houseNumber',
    ]).indexOf(field) > -1
  }
  else {
    return false;
  }
}

export const getStep3Validation = (state: DonationFormStepState<'3-PersonalForm'>, field: keyof Step3PersonalData): SingleValidatorResult =>
  state.validatorInput[field] !== undefined ? state.validatorResult.path(field) : singleValidatorResultBuilders.validating(field)

export const getStep2Validation = (state: DonationFormStepState<'2-AmountForm'>, field:keyof Step2PersonalData): SingleValidatorResult =>
  state.validatorInput[field] !== undefined ? state.validatorResult.path(field) : singleValidatorResultBuilders.validating(field)